import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useDropzone } from 'react-dropzone'
import { JSONFetcher, Button, DSIHtml, LogoSpinner, Modal, themeColor } from 'shared'
try {
  require('trix')
} catch (_err) {}
import 'trix/dist/trix.css'
import {
  EditorElement,
  ImagesButtonAndModalProps,
  ImageSelectorProps,
  ImageUploadButtonProps,
  Img,
  TrixEditorProps,
  TrixToolbarProps,
} from './types'

const generateId = () => `trix-input-${Math.round(Math.random() * 10000)}`

export const TrixEditor = (props: TrixEditorProps): JSX.Element => {
  const hiddenInputId = useMemo(() => props.hiddenField ?? generateId(), [props.hiddenField])
  const editorElement: React.MutableRefObject<HTMLElement | null> = useRef(null)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(editorElement.current as any)?.addEventListener('trix-change', (event: Event) => {
      const change = (event.target as HTMLElement).innerHTML
      props.onChange && props.onChange(change)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Toolbar {...props} />
      <trix-editor toolbar="trix-toolbar" input={hiddenInputId} ref={editorElement} />
      {!props.hiddenField && <input type="hidden" id={hiddenInputId} value={props.value} />}
    </>
  )
}

const Toolbar = ({ imagesUrl, imageUploadUrl }: TrixToolbarProps) => {
  return (
    <trix-toolbar className="trix-toolbar" id="trix-toolbar">
      <div className="trix-button-row">
        <span className="trix-button-group trix-button-group--text-tools" data-trix-button-group="text-tools">
          <button
            className="trix-button trix-button--icon trix-button--icon-bold"
            data-trix-attribute="bold"
            data-trix-key="b"
            tabIndex={-1}
            type="button"
          >
            {I18n.t('modules.trix_editor.button_bold')}
          </button>
          <button
            className="trix-button trix-button--icon trix-button--icon-italic italic"
            data-trix-attribute="italic"
            data-trix-key="i"
            tabIndex={-1}
            type="button"
          >
            {I18n.t('modules.trix_editor.button_italic')}
          </button>
          <button
            className="trix-button trix-button--icon trix-button--icon-bullet-list list bullets"
            data-trix-attribute="bullet"
            tabIndex={-1}
            type="button"
          >
            <span>···</span>
            {I18n.t('modules.trix_editor.button_list')}
          </button>
          <button
            className="trix-button trix-button--icon trix-button--icon-link link"
            data-trix-action="link"
            data-trix-attribute="href"
            data-trix-key="k"
            type="button"
          >
            {I18n.t('modules.trix_editor.button_link')}
          </button>
          {(imagesUrl || imageUploadUrl) && <VideosButtonAndModal />}
          {imagesUrl && <ImagesButtonAndModal imagesUrl={imagesUrl} />}
          {imageUploadUrl && <ImageUploadButton imageUploadUrl={imageUploadUrl} />}
        </span>

        <button data-trix-action="undo" data-trix-key="z" style={{ display: 'none' }} type="button"></button>
        <button data-trix-action="redo" data-trix-key="shift+z" style={{ display: 'none' }} type="button"></button>
      </div>

      <div className="trix-dialogs" data-trix-dialogs="">
        <div className="trix-dialog trix-dialog--link" data-trix-dialog="href" data-trix-dialog-attribute="href">
          <div className="trix-dialog__link-fields">
            <input
              className="trix-input trix-input--dialog"
              data-trix-input=""
              name="href"
              placeholder="URL"
              type="url"
              disabled
            />
            <div className="trix-button-group">
              <input
                className="trix-button trix-button--dialog"
                data-trix-method="setAttribute"
                type="button"
                value={I18n.t('modules.trix_editor.button_add_link')}
              />
              <input
                className="trix-button trix-button--dialog"
                data-trix-method="removeAttribute"
                type="button"
                value={I18n.t('modules.trix_editor.button_unlink')}
              />
            </div>
          </div>
        </div>
      </div>
    </trix-toolbar>
  )
}

const ImagesButtonAndModal = ({ imagesUrl }: ImagesButtonAndModalProps): JSX.Element => {
  const [visiblePhotoModal, setVisiblePhotoModal] = useState(false)
  const openPhotoModal = useCallback(() => setVisiblePhotoModal(true), [])
  const hidePhotoModal = useCallback(() => setVisiblePhotoModal(false), [])
  const [loading, setLoading] = useState(false)

  const [images, setImages] = useState<Array<Img>>([])

  useEffect(() => {
    JSONFetcher.load<Array<Img>>({ url: imagesUrl, success: setImages })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDrop = useCallback(
    (acceptedFiles: Array<File>) => {
      acceptedFiles.forEach((file) => {
        setLoading(true)
        const formData = new FormData()
        formData.append('file', file)

        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
        fetch(imagesUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-CSRF-Token': token },
          body: formData,
        })
          .then<Array<Img>>((response) => response.json())
          .then((json) => {
            setImages(json)
            setLoading(false)
          })
      })
    },
    [imagesUrl]
  )

  const { getRootProps, getInputProps } = useDropzone({ onDrop })

  return (
    <>
      <button className="trix-button" type="button" onClick={openPhotoModal}>
        {I18n.t('modules.trix_editor.button_picture_insert')}
      </button>

      <Modal
        id="photo-modal"
        onHide={hidePhotoModal}
        show={visiblePhotoModal}
        header={I18n.t('modules.trix_editor.picture_modal_message.title')}
      >
        <div {...getRootProps()} className="mb-6">
          <input {...getInputProps()} />
          <Button loading={loading} type="button" className="btn btn-small btn-primary">
            {I18n.t('modules.trix_editor.picture_modal_message.choose_files_button')}
          </Button>
        </div>
        {images ? (
          <p>{I18n.t('modules.trix_editor.picture_modal_message.explanation_html')}</p>
        ) : (
          <p className="my-2">{I18n.t('modules.trix_editor.picture_modal_message.zero_pictures_warning')}</p>
        )}
        <div className="flex flex-wrap my-2">
          {images.map((img, index) => (
            <ImageSelector {...img} key={index} closeModal={hidePhotoModal} />
          ))}
        </div>
      </Modal>
    </>
  )
}

const LoadingStyle: React.CSSProperties = {
  color: 'transparent',
  textShadow: 'none',
  lineHeight: 0,
  transition: 'none',
}

const ImageUploadButton = ({ imageUploadUrl }: ImageUploadButtonProps): JSX.Element => {
  const [loading, setLoading] = useState(false)

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (!event.target.files) return
      setLoading(true)
      const formData = new FormData()
      formData.append('file', event.target.files[0])

      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''

      fetch(imageUploadUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': token },
        body: formData,
      })
        .then<Img>((response) => response.json())
        .then((json) => {
          // TODO: This breaks pages with multiple TrixEditor instances.
          const trixEditor = (document.getElementsByTagName('trix-editor')[0] as EditorElement).editor
          trixEditor.insertString(json.url)
          trixEditor.insertLineBreak()
          trixEditor.insertLineBreak()
          setLoading(false)
        })
    },
    [imageUploadUrl]
  )

  return (
    <>
      <label
        htmlFor="image-upload"
        className="trix-button flex items-center justify-center"
        style={(loading && LoadingStyle) || undefined}
      >
        {loading && <LogoSpinner color={themeColor('green-500')} height={20} style={{ position: 'absolute' }} />}
        {I18n.t('modules.trix_editor.button_picture_insert')}
      </label>
      <input
        type="file"
        id="image-upload"
        multiple={false}
        style={{ position: 'absolute', top: '-10vh', left: 0 }}
        onChange={handleChange}
      />
    </>
  )
}

const ImageSelector = ({ insert = '', closeModal, thumb }: ImageSelectorProps): JSX.Element => {
  const handleClick = useCallback(() => {
    // TODO: This would break pages with multiple TrixEditor instances.
    const trixEditor = (document.getElementsByTagName('trix-editor')[0] as EditorElement).editor
    trixEditor.insertString(insert)
    trixEditor.insertLineBreak()
    trixEditor.insertLineBreak()
    closeModal()
  }, [closeModal, insert])

  return (
    <span className="trix-image-selector" onClick={handleClick}>
      <img src={thumb}></img>
    </span>
  )
}

const VideosButtonAndModal = (): JSX.Element => {
  const [visibleVideoModal, setVisibleVideoModal] = useState(false)
  const showVideoModal = useCallback(() => setVisibleVideoModal(true), [])
  const hideVideoModal = useCallback(() => setVisibleVideoModal(false), [])

  return (
    <>
      <button className="trix-button" type="button" onClick={showVideoModal}>
        {I18n.t('modules.trix_editor.button_video_insert')}
      </button>

      <Modal
        id="video-modal"
        onHide={hideVideoModal}
        show={visibleVideoModal}
        header={I18n.t('modules.trix_editor.video_modal_message.title')}
      >
        <DSIHtml value={I18n.t('modules.trix_editor.video_modal_message.explanation_html')} />
      </Modal>
    </>
  )
}
